import React, { memo, useContext, useEffect, useReducer } from 'react';
import { useSnackbar } from 'notistack';
import { useParams, Redirect } from 'react-router-dom';
import { isValidObject, isValidString } from '../../../catalog/Validity';
import { context } from '../../../globalState/GlobalState';
import { createCustomDescriptionTag, createCustomKeywordsTag, createCustomTitleTag } from '../uiElements/helmet/MetaTags';
import TitleAndDescriptionTags from '../uiElements/helmet/TitleAndDescriptionTags';
import { sendProductAnalytics } from '../../../catalog/Analytics';
import { fetchTicketAPI } from '../../apiCalls/TicketAPICalls';
import Ticket1 from '../../designs/ticket1/Ticket1';
import { TicketEventType } from '../../../catalog/enums/TicketEventType';

const initialState = {
    title: '',
    shortDescription: '',
    featureImage: '',
    prices: [],
    ticketsTotal: 0,
    ticketsSold: 0,
    ticketsHold: 0,
    eventTitle: '',
    eventType: TicketEventType.None,
    eventFeatureImage: '',
    eventVenue: '',
    timeOfEvent: new Date(),
    endTimeOfEvent: new Date(),
    stripeProductId: '',
    published: false,
    inStock: false,
    deleted: true,
    loadingSpinner: true,
    error: false,
};

function reducer(state, action) {
    switch (action.type) {
        case 'fetchProductTitle':
            return { ...state, title: action.title };
        case 'fetchTicket':
            return { ...state,
                title: action.title,
                shortDescription: action.shortDescription,
                featureImage: action.featureImage,
                prices: action.prices,
                ticketsTotal: action.ticketsTotal,
                ticketsSold: action.ticketsSold,
                ticketsHold: action.ticketsHold,
                eventId: action.eventId,
                eventTitle: action.eventTitle,
                eventType: action.eventType,
                eventFeatureImage: action.eventFeatureImage,
                eventVenue: action.eventVenue,
                timeOfEvent: action.timeOfEvent,
                endTimeOfEvent: action.endTimeOfEvent,
                stripeProductId: action.stripeProductId,
                published: action.published,
                inStock: action.inStock,
                deleted: action.deleted,
            };
        case 'setLoadingSpinner':
            return { ...state, loadingSpinner: action.loadingSpinner };
        case 'setError':
            return { ...state, error: true };
        default:
            return initialState;
    }
}

const Ticket = () => {
    const params = useParams();
    const [state, setState] = useReducer(reducer, initialState);
    const { globalState, setGlobalState } = useContext(context);
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        sendProductAnalytics(globalState, state.title, params.productid);
    }, [globalState, state.title]);

    useEffect(() => {
        async function fetchProduct() {
            if (isValidObject(globalState.accountId)) {
                setState({ type: 'setLoadingSpinner', loadingSpinner: true });
                await fetchTicketAPI(globalState.accountId, params.ticketid, setState, setGlobalState, enqueueSnackbar);
                setState({ type: 'setLoadingSpinner', loadingSpinner: false });
            }
        }

        fetchProduct();
    }, [globalState.accountId, params.productid]);

    const getTicketDesign = () => {
        if (!isValidObject(globalState) || !isValidObject(globalState.websiteStyles)) {
            return null;
        }

        return (
            <Ticket1
                id={params.ticketid}
                title={state.title}
                shortDescription={state.shortDescription}
                featureImage={state.featureImage}
                prices={state.prices}
                ticketsTotal={state.ticketsTotal}
                ticketsSold={state.ticketsSold}
                ticketsHold={state.ticketsHold}
                eventId={state.eventId}
                eventTitle={state.eventTitle}
                eventType={state.eventType}
                eventFeatureImage={state.eventFeatureImage}
                eventVenue={state.eventVenue}
                timeOfEvent={state.timeOfEvent}
                endTimeOfEvent={state.endTimeOfEvent}
                stripeProductId={state.stripeProductId}
                published={state.published}
                inStock={state.inStock}
                deleted={state.deleted}
                loadingSpinner={state.loadingSpinner}
            />
        );
    };

    if (state.error) {
        return <Redirect to="/notfound" />;
    }

    return (
        <>
            {isValidString(state.title) ? (
                <>
                    <TitleAndDescriptionTags
                        title={createCustomTitleTag(state.title, globalState)}
                        description={createCustomDescriptionTag(`Check out this product - ${state.title}`, globalState)}
                        keywords={createCustomKeywordsTag(`Store, Product, ${state.title}`, globalState)}
                    />
                    {/* <ProductTags id={params.productid} title={state.title} productTime={state.timeOfProduct} featureImage={state.featureImage} writerName={state.author} /> */}
                </>
            ) : null}
            {getTicketDesign()}
        </>
    );
};

export default memo(Ticket);
